import { useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw, X, Check } from 'lucide-react';
import { Button } from '../ui/Button';

/**
 * Modal that captures one photo of an inductee at reception. Uses the
 * native file-input + `capture="environment"` so the OS camera opens
 * directly (no getUserMedia permission plumbing, no browser-cam shim).
 *
 * Pipeline: file → bitmap → 1024px-max canvas → JPEG data URL.
 * The compression step matters: a raw phone snap is 3–5MB, vastly more
 * than we need for a badge-sized thumbnail. Resizing to 1024px keeps
 * uploads small without sacrificing recognisability.
 *
 * The retake button is always available before saving, so staff can
 * reshoot if the first try is blurry or unflattering.
 */
export function PhotoCaptureModal({
  open,
  personName,
  onClose,
  onSave,
  eyebrow,
  title,
  hint,
}: {
  open: boolean;
  personName: string;
  onClose: () => void;
  /** Receives a base64 JPEG data URL (data:image/jpeg;base64,...). */
  onSave: (dataUrl: string) => void | Promise<void>;
  /** Override the header eyebrow (default "Photo"). */
  eyebrow?: string;
  /** Override the heading (default "Photo of {personName}"). */
  title?: string;
  /** Override the guidance line under the heading. */
  hint?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Esc to close. Reset state every time the modal closes so a
  // subsequent open starts on a clean slate.
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setError(null);
      setProcessing(false);
      setSaving(false);
      return;
    }
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onClose();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, saving]);

  if (!open) return null;

  async function handleFile(file: File) {
    setError(null);
    setProcessing(true);
    try {
      const dataUrl = await compressImage(file, 1024, 0.85);
      setPreview(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the photo');
    } finally {
      setProcessing(false);
    }
  }

  function openCamera() {
    fileInputRef.current?.click();
  }

  async function save() {
    if (!preview) return;
    setSaving(true);
    try {
      await onSave(preview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the photo');
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-capture-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-start justify-between gap-3 border-b border-divider/10 px-6 py-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
              <Camera className="h-3.5 w-3.5" />
              <span>{eyebrow ?? 'Photo'}</span>
            </div>
            <h2
              id="photo-capture-title"
              className="mt-1 text-lg font-semibold tracking-tight text-fg"
            >
              {title ?? `Photo of ${personName}`}
            </h2>
            <p className="mt-1 text-sm text-fg-muted">
              {hint ??
                'Hold the device facing the inductee and tap to take the photo. Retake if you need a clearer shot.'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            Close
          </Button>
        </header>

        <div className="bg-surface-2 p-4 sm:p-6">
          {preview ? (
            <div className="overflow-hidden rounded-token-sm border border-line/10 bg-black">
              <img
                src={preview}
                alt={`Photo of ${personName}`}
                className="mx-auto block max-h-80 w-auto"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={openCamera}
              disabled={processing}
              className="flex h-64 w-full flex-col items-center justify-center gap-3 rounded-token-sm border-2 border-dashed border-line-strong/30 bg-surface text-fg-muted transition-colors hover:border-accent/50 hover:bg-accent/5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Camera className="h-10 w-10 text-fg-dim" />
              <span className="text-sm font-medium">
                {processing ? 'Loading photo...' : 'Tap to open camera'}
              </span>
              <span className="text-xs text-fg-dim">
                Or pick a photo from this device
              </span>
            </button>
          )}

          {/* Native camera trigger. capture="environment" opens the rear
              camera on phones/tablets — right for staff facing the inductee. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              // Reset so picking the same file again still fires onChange
              e.target.value = '';
            }}
          />

          {error ? (
            <div className="mt-3 rounded-token-sm border border-danger/30 bg-danger-soft/15 px-3 py-2 text-xs text-danger-soft-fg">
              {error}
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-divider/10 bg-surface-2 px-4 py-3 sm:px-6">
          <Button
            variant="ghost"
            size="md"
            iconLeft={<RefreshCw className="h-4 w-4" />}
            onClick={openCamera}
            disabled={!preview || processing || saving}
          >
            Retake
          </Button>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={saving}
            >
              {preview ? 'Cancel' : 'Skip'}
            </Button>
            <Button
              variant="primary"
              size="md"
              iconLeft={<Check className="h-4 w-4" />}
              onClick={save}
              disabled={!preview || processing}
              loading={saving}
            >
              Save photo
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Loads a File into a canvas, resizes so its longest edge is at most
 * `maxEdge`, returns a JPEG data URL at the given quality. Used to
 * shrink raw camera-roll photos before upload.
 */
async function compressImage(
  file: File,
  maxEdge: number,
  quality: number,
): Promise<string> {
  const bitmap = await fileToBitmap(file);
  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

/** createImageBitmap is fast and decodes off the main thread when available; fall back to <img>. */
async function fileToBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to <img> path
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}
